import { Alert, Box, Checkbox, Divider, FormControlLabel, Typography } from '@mui/material';

import type { IntegrationCatalogEntry } from '@/api/integrations';

import { WebhookUrls } from '../WebhookUrls';
import type { BookingWizardData } from './types';

interface Props {
  booking: BookingWizardData;
  onBooking: (partial: Partial<BookingWizardData>) => void;
  entry: IntegrationCatalogEntry | undefined;
  toCode: string;
}

export function Step5Webhook({ booking, onBooking, entry, toCode }: Props) {
  const mode = entry?.webhook_setup_mode ?? 'none';
  const name = entry?.name ?? 'the provider';
  const restech = entry?.id ?? booking.restech ?? '';
  const slug = booking.account_mode === 'multi' ? booking.slug || null : null;

  function control() {
    if (mode === 'api') {
      return (
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={booking.installWebhook}
                onChange={(e) => onBooking({ installWebhook: e.target.checked })}
              />
            }
            label={`Install a legacy webhook in ${name} after creation`}
          />
          <Typography variant="caption" color="text.secondary" display="block">
            Not recommended. {name} is retiring legacy webhooks; booking updates should arrive
            through notification subscriptions (Subscriptions page). If enabled, we create the
            webhook via the {name} API (using the endpoints below) and store the generated auth
            token. Only this operator&apos;s own previous webhooks are replaced.
          </Typography>
        </Box>
      );
    }
    if (mode === 'email') {
      return (
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={booking.openEmail}
                onChange={(e) => onBooking({ openEmail: e.target.checked })}
              />
            }
            label={`Open the webhook-setup request to ${name} support after creation`}
          />
          <Typography variant="caption" color="text.secondary" display="block">
            You review the standard request there before anything is sent. You can also open it
            later from the integration&apos;s Webhooks tab.
          </Typography>
        </Box>
      );
    }
    if (mode === 'manual') {
      return (
        <Alert severity="info">
          Set up the webhook manually in {name}&apos;s dashboard using the endpoint URLs below.
        </Alert>
      );
    }
    return <Alert severity="info">This integration does not receive webhooks.</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {control()}
      {mode !== 'none' && restech && (
        <>
          <Divider />
          <WebhookUrls toCode={toCode} restech={restech} slug={slug} />
        </>
      )}
    </Box>
  );
}
